import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Button } from '@immo/ui-kit';
import type { GetApiCallResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { KeyIcon } from '@/components/layout/icons';
import { ResourceHeader } from '@/components/resource-header';

type RequestDetailData = {
  readonly request: GetApiCallResponse;
};

// Addressed by the entry's own id — no key segment. An entry whose API key never
// resolved has no owning key to be addressed under, which is precisely the
// traffic this page exists to make reachable (IMM-494.2).
export async function apiKeyRequestDetailLoader({ params }: LoaderFunctionArgs): Promise<RequestDetailData> {
  const requestId = params['requestId'];

  if (typeof requestId !== 'string' || requestId.length === 0) {
    throw new Response('Missing request id', { status: 404 });
  }

  const result = await apiClient.adminApiCalls.get({ pathParams: { callId: requestId } });

  if (!result.ok) {
    throw new Response('Failed to load API request', { status: result.status });
  }

  return { request: result.body };
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

// Bodies are stored as text (JSON payloads). Pretty-print when parseable, else
// show the raw stored text verbatim — never re-process (Business Rule: shown as
// stored).
function prettyBody(body: string | null): string | null {
  if (body === null) return null;

  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function labelFor(name: string | null, id: string | null, fallback: string): string {
  if (name !== null && name !== '') return name;

  if (id !== null && id !== '') return id;

  return fallback;
}

export function ApiKeyRequestDetailPage() {
  const t = useTranslations('pages.admin.api-keys');
  const tPage = useTranslations('pages.admin.api-key-requests');
  const tDetail = useTranslations('pages.admin.api-keys.request-detail');
  const tRequests = useTranslations('pages.admin.api-keys.requests');
  const tReplay = useTranslations('pages.admin.api-keys.replay');
  const intl = useIntl();
  const { request } = useLoaderData() as RequestDetailData;
  const revalidator = useRevalidator();

  const [replaying, setReplaying] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);

  const requestBody = prettyBody(request.requestBody);
  const responseBody = prettyBody(request.responseBody);

  // Whether the button is offered is decided server-side (`replayable`) from the
  // same domain rule the write path enforces — the admin app must never
  // re-derive it, or the two disagree and you get a dead button or a surprise
  // 422.
  const replay = async () => {
    setReplaying(true);
    setReplayError(null);

    try {
      // Replay stays key-scoped: re-submitting runs as the original API key.
      // The key is read from the loaded entry rather than the address, and is
      // never null here — an entry with no key is never `replayable` (its
      // absent key row makes `replayBlockedReason` return API_KEY_REVOKED).
      if (request.apiKeyId === null) return;

      const result = await apiClient.adminApiCalls.replay({ pathParams: { callId: request.id }, body: {} });

      if (result.ok && result.body.succeeded) {
        // Re-reads the entry so it comes back marked replayed — the state that
        // stops a second submission creating a second lead.
        revalidator.revalidate();

        return;
      }

      setReplayError(result.status === 403 ? tReplay('forbidden-error') : tReplay('generic-error'));
    } catch {
      setReplayError(tReplay('generic-error'));
    } finally {
      setReplaying(false);
    }
  };

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={KeyIcon}
            crumbs={[
              { label: tPage('title'), to: '/logs/api-key-requests' },
              { label: `${request.method} ${request.endpoint}` },
            ]}
          />
        }
      />
      <ResourceHeader
        icon={KeyIcon}
        title={`${request.method} ${request.endpoint}`}
        // Always the ORIGINAL call. The mark says a replay exists; the replay
        // list below says what came of it.
        meta={`${request.statusCode} · ${tRequests(`outcomes.${request.outcome}`)}${
          request.replays.length === 0 ? '' : ` · ${tReplay('mark')}`
        }`}
      />
      <div className="space-y-8 px-6 pb-8">
        {/* Recovery (IMM-458): re-submits this exact payload through the current
            contract. Offered only when the server says the entry is replayable;
            otherwise the reason is shown so an operator working a backlog knows
            why this one is not actionable. */}
        <section className="space-y-2 rounded-md border border-border p-4">
          <SectionHeading>{tReplay('heading')}</SectionHeading>
          {request.replays.length > 0 && (
            // The attempt history, oldest first. A history rather than a single
            // marker because a failed replay may be retried — and because
            // "someone tried and it failed" is itself something an operator
            // needs to see.
            <ul className="space-y-1">
              {request.replays.map((attempt) => (
                <li key={attempt.id} className="text-sm text-text-secondary">
                  {intl.formatDate(attempt.attemptedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  {' · '}
                  {attempt.outcome === null
                    ? tReplay('in-flight')
                    : `${attempt.statusCode ?? '—'} · ${tRequests(`outcomes.${attempt.outcome}`)}`}
                </li>
              ))}
            </ul>
          )}
          {request.replayable ? (
            <div className="space-y-2">
              <p className="text-sm text-text-secondary">{tReplay('description')}</p>
              <Button onClick={replay} disabled={replaying}>
                {replaying ? tReplay('submitting') : tReplay('action')}
              </Button>
              {replayError !== null && <p className="text-sm text-danger-600">{replayError}</p>}
            </div>
          ) : (
            <p className="text-sm text-text-secondary">
              {tReplay(`blocked.${request.replayBlockedReason ?? 'ENDPOINT_NOT_REPLAYABLE'}`)}
            </p>
          )}
        </section>

        <section className="space-y-2">
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{tDetail('id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.id}</dd>
            <dt className="text-text-tertiary">{tDetail('request-id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.requestId}</dd>
            <dt className="text-text-tertiary">{tDetail('occurred-at-label')}</dt>
            <dd className="text-text-primary">
              {intl.formatDate(request.occurredAt, { dateStyle: 'long', timeStyle: 'medium' })}
            </dd>
            <dt className="text-text-tertiary">{tDetail('method-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.method}</dd>
            <dt className="text-text-tertiary">{tDetail('endpoint-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.endpoint}</dd>
            <dt className="text-text-tertiary">{tDetail('status-label')}</dt>
            <dd className="text-text-primary tabular-nums">{request.statusCode}</dd>
            <dt className="text-text-tertiary">{tDetail('outcome-label')}</dt>
            <dd className="text-text-primary">{tRequests(`outcomes.${request.outcome}`)}</dd>
            <dt className="text-text-tertiary">{tDetail('duration-label')}</dt>
            <dd className="text-text-primary tabular-nums">{tDetail('duration-value', { ms: request.durationMs })}</dd>
            <dt className="text-text-tertiary">{tDetail('permission-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.permission ?? '—'}</dd>
          </dl>
        </section>

        <section className="space-y-2">
          <SectionHeading>{tDetail('origin-heading')}</SectionHeading>
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{tDetail('workspace-label')}</dt>
            <dd className="text-text-primary">{labelFor(request.workspaceName, request.workspaceId, t('unknown'))}</dd>
            <dt className="text-text-tertiary">{tDetail('api-key-label')}</dt>
            <dd className="text-text-primary">{labelFor(request.apiKeyName, request.apiKeyId, t('unknown'))}</dd>
            <dt className="text-text-tertiary">{tDetail('member-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.memberId ?? '—'}</dd>
            <dt className="text-text-tertiary">{tDetail('client-ip-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{request.clientIp ?? '—'}</dd>
            <dt className="text-text-tertiary">{tDetail('user-agent-label')}</dt>
            <dd className="text-text-primary">{request.userAgent ?? '—'}</dd>
          </dl>
        </section>

        <section className="space-y-2">
          <SectionHeading>{tDetail('request-body-heading')}</SectionHeading>
          {requestBody === null ? (
            <p className="text-text-tertiary">{tDetail('no-body')}</p>
          ) : (
            <pre className="max-h-96 overflow-auto rounded-sm border border-border bg-subtle p-3 font-mono text-sm text-text-primary">
              {requestBody}
            </pre>
          )}
        </section>

        <section className="space-y-2">
          <SectionHeading>{tDetail('response-body-heading')}</SectionHeading>
          {responseBody === null ? (
            <p className="text-text-tertiary">{tDetail('no-body')}</p>
          ) : (
            <pre className="max-h-96 overflow-auto rounded-sm border border-border bg-subtle p-3 font-mono text-sm text-text-primary">
              {responseBody}
            </pre>
          )}
        </section>
      </div>
    </>
  );
}
